'use client';
/**
 * L'INTERPRÉTATION BIOLOGIQUE D'UN EXAMEN.
 *
 * ── Le défaut qu'il comble ─────────────────────────────────────────────────
 *
 * RQ, 25/08/2026 : « je ne vois pas la validation biologique non plus sur
 * l'examen profil hépatite ».
 *
 * Elle avait raison, et la cause était nette : la route
 * `GET /ged/interpretations/pour-examen/:code` existait, répondait parfaitement
 * — le jeu `VHB-SERO`, ses trois paramètres, ses sept règles — et **aucun écran
 * ne l'appelait**. Écrite, jamais branchée. Le dossier d'un examen n'avait pas
 * d'onglet pour ça ; l'onglet « Interprétation » du référentiel est l'ÉDITEUR
 * des jeux de règles, pas la vue d'un examen.
 *
 * ── Ce que cet onglet montre, et pourquoi il ne montre pas plus ────────────
 *
 * ⚠️ UN EXAMEN N'EST PAS PROPRIÉTAIRE D'UN JEU DE RÈGLES. Une règle porte des
 * PARAMÈTRES, jamais un examen — décision du 24/08, et le cas qui l'impose est
 * précisément l'hépatite B : chez BIO24 elle n'est pas un examen à plusieurs
 * composantes mais SEPT examens d'un paramètre chacun, alors que
 * l'interprétation croise Ag HBs, anti-HBc et anti-HBs. Une règle attachée à
 * « son » examen aurait dû être recopiée sur chacun des trois.
 *
 * Cet onglet AFFICHE donc les jeux dont l'examen porte au moins un paramètre.
 * C'est un rendu, pas un rattachement — et il le dit, sinon on croirait pouvoir
 * modifier ici ce qui vit ailleurs.
 *
 * ⚠️ IL N'ÉCRIT RIEN. Les règles se rédigent dans l'onglet « Interprétation »
 * du référentiel, où la couverture des combinaisons est éprouvée avant mise en
 * service. Un éditeur de plus, ouvert depuis chaque examen, ferait modifier un
 * jeu partagé sans voir les autres examens qui l'affichent.
 */
import React, { useEffect, useState } from 'react';
import { card, EmptyState, Skeleton } from '@/components/ui';
import { moteur } from './Registres';
import { ArbreDecision, type Noeud } from './ArbreDecision';

interface Condition {
  parametre: string;
  operateur: 'positif' | 'negatif' | 'inferieur' | 'superieurOuEgal';
  seuil?: number;
}
interface Regle {
  code: string;
  libelle: string;
  conditions: Condition[];
  requis?: string[];
  commentaire?: string;
  reserve?: string;
}
interface Jeu {
  documentId: string;
  /** L'arbre voyage AVEC le jeu : un second aller-retour ferait clignoter le
   *  diagramme sous le texte. */
  arbre?: Noeud | null;
  code: string;
  libelle: string;
  parametres: string[];
  regles: Regle[];
  actif?: boolean;
  note?: string;
}

/** La condition, dite en français. Le vocabulaire du moteur (`superieurOuEgal`)
 *  n'a pas à remonter jusqu'au biologiste. */
function conditionLisible(c: Condition): string {
  switch (c.operateur) {
    case 'positif': return `${c.parametre} positif`;
    case 'negatif': return `${c.parametre} négatif`;
    case 'inferieur': return `${c.parametre} < ${c.seuil}`;
    case 'superieurOuEgal': return `${c.parametre} ≥ ${c.seuil}`;
    default: return c.parametre;
  }
}

export function OngletInterpretation({ code, parametresDeLExamen }: {
  code: string;
  /** Les paramètres que l'examen porte — pour dire lesquels il apporte au jeu. */
  parametresDeLExamen: string[];
}) {
  const [jeux, setJeux] = useState<Jeu[] | null>(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     Le `setState` synchrone ici n'ajuste pas un état à une entrée : il EFFACE
     la donnée précédente avant d'aller en chercher une autre. Sans lui, l'écran
     afficherait les données de la fiche PRÉCÉDENTE pendant tout le temps de la
     requête — un faux vide à l'envers, qui affirme sur la nouvelle fiche ce qui
     était vrai de l'ancienne. C'est le seul cas où l'on veut ce rendu
     intermédiaire, et il ne se dérive pas : la donnée vient du réseau.
     ⚠️ Silence CIBLÉ, ligne à ligne. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
    setJeux(null); setErreur('');
    void moteur(`interpretations/pour-examen/${encodeURIComponent(code)}`).then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message || 'Interprétation indisponible.'); return; }
      setJeux((r.data.jeux || []) as Jeu[]);
    });
    return () => { vivant = false; };
  }, [code]);

  if (erreur) return <EmptyState titre="Interprétation indisponible" indice={erreur} />;
  if (!jeux) return <Skeleton rows={4} />;

  if (!jeux.length) {
    return (
      <EmptyState
        titre="Aucune règle d’interprétation ne concerne cet examen"
        indice="Un jeu de règles s’affiche ici dès qu’il porte l’un des paramètres de cet examen. Les règles se rédigent dans l’onglet « Interprétation » du référentiel — une règle porte des paramètres, jamais un examen."
      />
    );
  }

  const portes = new Set(parametresDeLExamen);

  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 10 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '92ch' }}>
        Ce que le biologiste peut proposer au compte rendu à partir des résultats <strong>combinés</strong> des
        paramètres. Un jeu s’affiche ici dès que cet examen porte l’un de ses paramètres — c’est un
        <strong> rendu</strong>, pas un rattachement : une règle porte des paramètres, jamais un examen.
        Les règles se rédigent dans l’onglet <strong>Interprétation</strong> du référentiel.
      </p>

      {jeux.map((j) => {
        const manquants = (j.parametres || []).filter((p) => !portes.has(p));
        return (
          <div key={j.documentId || j.code} style={{ ...card, padding: 14 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{j.code}</span>
              <strong style={{ fontSize: 13.5 }}>{j.libelle}</strong>
              {/* ⚠️ « Hors service » n'est pas un détail : un jeu inactif ne
                  propose RIEN au biologiste. L'afficher comme les autres ferait
                  croire que le commentaire remonte au compte rendu. */}
              {j.actif === false && (
                <span title="Ce jeu n’est pas en service : il ne propose aucun commentaire à la validation."
                  style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>
                  hors service
                </span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {j.regles.length} règle{j.regles.length > 1 ? 's' : ''}
              </span>
            </div>

            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Croise : {(j.parametres || []).map((p) => (
                <span key={p} style={{
                  fontFamily: 'var(--font-mono)', fontSize: 12.5,
                  fontWeight: portes.has(p) ? 700 : 400,
                  color: portes.has(p) ? 'var(--text-strong)' : 'var(--amber-600)',
                  marginInlineEnd: 8,
                }}>{p}</span>
              ))}
            </div>
            {/* ⚠️ CE QUE CET EXAMEN N'APPORTE PAS. Sans cette phrase, on lirait
                le jeu comme s'il concluait à partir de ce seul examen — alors
                qu'il lui manque des paramètres prescrits ailleurs. */}
            {!!manquants.length && (
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
                Cet examen n’apporte pas {manquants.join(', ')} : la conclusion suppose que ce ou ces
                paramètres soient prescrits par ailleurs.
              </p>
            )}

            <div style={{ display: 'grid', gap: 7, marginTop: 10 }}>
              {j.regles.map((r) => (
                <div key={r.code} style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 11px' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.libelle}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, fontFamily: 'var(--font-mono)' }}>
                    {(r.conditions || []).map(conditionLisible).join('  et  ')}
                  </div>
                  {r.commentaire && (
                    <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.55 }}>{r.commentaire}</p>
                  )}
                  {/* La réserve dit ce que la règle NE PEUT PAS trancher. C'est
                      la partie qu'un biologiste doit lire avant de reprendre le
                      commentaire à son compte. */}
                  {r.reserve && (
                    <p style={{ margin: '5px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--amber-600)' }}>
                      <strong>Réserve — </strong>{r.reserve}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* L'arbre, sous les règles : on lit la liste, puis on voit ce
                qu'elle produit. L'inverse ferait chercher dans le diagramme ce
                qu'une phrase dit mieux. */}
            <div style={{ marginTop: 10 }}>
              <ArbreDecision arbre={j.arbre ?? null} titre="Arbre de décision" />
            </div>

            {j.note && (
              <p style={{ margin: '9px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>{j.note}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
